/**
 * Checkout order review: multi-warehouse line split (qty + warehouse per row).
 * Syncs hidden po_items[itemRow][apiRow] inputs and validates row qty sum vs line total.
 * Also recalculates line subtotals, per-order totals, and the grand total on qty change.
 */

import { formatCurrency } from '../core/utilities.js';

function parseApis($container) {
    const raw = $container.attr('data-apis');
    if (!raw) {
        return [];
    }
    try {
        return JSON.parse(raw);
    } catch (e) {
        return [];
    }
}

function getOtherSelectedIds($container, $currentRow) {
    const ids = [];
    $container.find('.order-wh-row').each(function () {
        if (this !== $currentRow[0]) {
            const v = $(this).find('.order-wh-select').val();
            if (v) {
                ids.push(String(v));
            }
        }
    });
    return ids;
}

function populateSelect($select, apis, selectedId, excludeIds) {
    const exclude = new Set((excludeIds || []).map(String));
    const sel = selectedId != null && selectedId !== '' ? String(selectedId) : '';
    $select.empty();
    apis.forEach((api) => {
        const idStr = String(api.id);
        if (idStr !== sel && exclude.has(idStr)) {
            return;
        }
        const stock = api.stock != null ? api.stock : '—';
        const label = api.label + ' (' + stock + ' in stock)';
        $select.append(
            $('<option></option>').attr('value', idStr).text(label)
        );
    });
    if (sel) {
        $select.val(sel);
    }
}

function refreshAllSelects($container) {
    const apis = parseApis($container);
    $container.find('.order-wh-row').each(function () {
        const $row = $(this);
        const selected = $row.find('.order-wh-select').val();
        const otherSelected = getOtherSelectedIds($container, $row);
        populateSelect(
            $row.find('.order-wh-select'),
            apis,
            selected,
            otherSelected
        );
    });
}

function syncHiddens($container) {
    const itemRowId = $container.data('itemRowId');
    const apis = parseApis($container);
    const counts = {};
    apis.forEach((a) => {
        counts[a.id] = 0;
    });
    $container.find('.order-wh-row').each(function () {
        const id = $(this).find('.order-wh-select').val();
        const q = parseInt($(this).find('.order-wh-qty').val(), 10) || 0;
        if (id) {
            const n = parseInt(id, 10);
            counts[n] = (counts[n] || 0) + q;
        }
    });
    apis.forEach((api) => {
        const v = counts[api.id] || 0;
        const expectName =
            'po_items[' + itemRowId + '][' + api.id + ']';
        $container
            .find('input.js-wh-split-hidden')
            .filter(function () {
                return $(this).attr('name') === expectName;
            })
            .val(v);
    });
}

function rowSum($container) {
    let s = 0;
    $container.find('.order-wh-row').each(function () {
        s += parseInt($(this).find('.order-wh-qty').val(), 10) || 0;
    });
    return s;
}

function updateLineSubtotal($container) {
    const $row = $container.closest('tr.order-item');
    const unit = parseFloat($row.attr('data-unit-price'));
    if (!unit || isNaN(unit)) {
        return;
    }
    const qty = rowSum($container);
    const $sub = $row.find('.order-subtotal');
    if ($sub.length) {
        $sub.text(formatCurrency(unit * qty));
    }
}

function recalcOrderTableFooter($table) {
    let poTotal = 0;
    $table.find('tr.order-item').each(function () {
        const unit = parseFloat($(this).attr('data-unit-price'));
        if (!unit || isNaN(unit)) {
            return;
        }
        const $split = $(this).find('.order-split-qty--dynamic');
        const qty = $split.length ? rowSum($split) : parseInt($(this).find('.order-item__qty-pill').text(), 10) || 0;
        poTotal += unit * qty;
    });
    $table.find('.order-po-total').text(formatCurrency(poTotal));
}

function recalcCheckoutGrandTotal() {
    let grand = 0;
    $('.order-checkout-page .po-order').each(function () {
        const raw = $(this).find('.order-po-total').text().replace(/[^0-9.]/g, '');
        grand += parseFloat(raw) || 0;
    });
    $('.order-total-price').text(formatCurrency(grand));
    const $orderSubtotal = $('#orderSubtotal');
    if ($orderSubtotal.length) {
        $orderSubtotal.text(formatCurrency(grand));
    }
}

function recalcAfterSync($container) {
    updateLineSubtotal($container);
    const $table = $container.closest('table.po-order');
    if ($table.length) {
        recalcOrderTableFooter($table);
    }
    recalcCheckoutGrandTotal();
}

function initContainer($container) {
    const apis = parseApis($container);
    if (!apis.length) {
        return;
    }
    const totalQty = parseInt($container.data('totalQty'), 10) || 0;
    const defaultId = String($container.data('defaultApiId'));
    const $rows = $container.find('.order-wh-rows');
    const templateEl = $container.find('template.order-wh-row-template')[0];
    if (!templateEl) {
        return;
    }

    function updateRowActions() {
        const rows = $container.find('.order-wh-row');
        const n = rows.length;
        const allUsed = n >= apis.length;

        rows.each(function (i) {
            const $r = $(this);
            const isLast = i === n - 1;

            if (n <= 1) {
                $r.find('.order-wh-remove').addClass('d-none').attr('aria-hidden', 'true');
            } else {
                $r.find('.order-wh-remove').removeClass('d-none').attr('aria-hidden', 'false');
            }

            if (isLast && !allUsed) {
                $r.find('.order-wh-add').removeClass('d-none').attr('aria-hidden', 'false');
            } else {
                $r.find('.order-wh-add').addClass('d-none').attr('aria-hidden', 'true');
            }
        });
    }

    function bindRow($row) {
        $row.find('.order-wh-select').on('change', function () {
            refreshAllSelects($container);
            syncHiddens($container);
            recalcAfterSync($container);
        });
        $row.find('.order-wh-qty').on('input change', function () {
            syncHiddens($container);
            recalcAfterSync($container);
        });
        $row.find('.order-wh-remove').on('click', function () {
            if ($container.find('.order-wh-row').length <= 1) {
                return;
            }
            $row.remove();
            refreshAllSelects($container);
            syncHiddens($container);
            recalcAfterSync($container);
            updateRowActions();
        });
        $row.find('.order-wh-add').on('click', function () {
            const used = [];
            $container.find('.order-wh-select').each(function () {
                const v = $(this).val();
                if (v) {
                    used.push(String(v));
                }
            });
            const next = apis.find((a) => !used.includes(String(a.id)));
            if (!next) {
                return;
            }
            addRow(String(next.id), 0);
        });
    }

    function addRow(selectedId, qty) {
        const content = templateEl.content.cloneNode(true);
        const $row = $(content).find('.order-wh-row');
        $rows.append($row);
        $row.find('.order-wh-qty').val(qty);
        populateSelect(
            $row.find('.order-wh-select'),
            apis,
            selectedId,
            getOtherSelectedIds($container, $row)
        );
        bindRow($row);
        refreshAllSelects($container);
        syncHiddens($container);
        updateRowActions();
    }

    $rows.empty();
    addRow(defaultId, totalQty);
    recalcAfterSync($container);
}

/**
 * @returns {boolean} true if all dynamic split blocks pass sum validation
 */
export function validateCheckoutWarehouseSplit() {
    let ok = true;
    $('.order-split-qty--dynamic').each(function () {
        const $c = $(this);
        const total = parseInt($c.data('totalQty'), 10) || 0;
        syncHiddens($c);
        const sum = rowSum($c);
        const $err = $c.find('.order-wh-error');
        if (sum !== total) {
            ok = false;
            $err
                .removeClass('d-none')
                .text(
                    'Quantities must total ' +
                        total +
                        ' units for this line.'
                );
            $c.find('.order-wh-qty').first().trigger('focus');
        } else {
            $err.addClass('d-none').text('');
        }
    });
    return ok;
}

export function initCheckoutWarehouseSplit() {
    $('.order-split-qty--dynamic').each(function () {
        initContainer($(this));
    });
}

/**
 * After updating `data-apis` JSON on a multi-warehouse split container (e.g. async inventory),
 * refresh option labels and sync hidden inputs without re-binding row handlers.
 */
export function refreshCheckoutWarehouseSplitApis($container) {
    refreshAllSelects($container);
    syncHiddens($container);
    recalcAfterSync($container);
}
